import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import { ColorScheme } from '../../types'

const tokens = {
  dark: {
    bgOverlay: 'bg.overlay',
    textPrimary: 'text.primary',
    focusRing: 'interactive.focus-ring',
  },
  light: {
    bgOverlay: 'light.bg.overlay',
    textPrimary: 'light.text.primary',
    focusRing: 'light.interactive.focus-ring',
  },
}

export const overlayStyles = (
  colorScheme: ColorScheme,
  zIndex = 1000
): SystemStyleObject => ({
  position: 'fixed',
  inset: 0,
  zIndex,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: tokens[colorScheme].bgOverlay,
})

export const FOLD_W = '71px'
export const FOLD_H = '42px'

const POLYGON =
  `polygon(0 0, calc(100% - ${FOLD_W}) 0, 100% ${FOLD_H}, ` +
  `100% 100%, 0 100%)`

/**
 * Clip wrapper for the modal content. Lives inside an outer box that carries
 * the drop-shadow filter and positioning context.
 */
export const containerWrapperStyles: SystemStyleObject = {
  position: 'relative',
  display: 'inline-flex',
  clipPath: POLYGON,
  zIndex: 1,
}

/**
 * Sibling of containerWrapperStyles — NOT a child, so it is unaffected by the
 * content wrapper's clip-path. Extends 2.649 px beyond the content wrapper on
 * every side and uses the same polygon, producing a correctly traced stroke
 * (including the diagonal cut) when the content wrapper sits on top.
 */
export const borderLayerStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  position: 'absolute',
  top: '-2.649px',
  left: '-2.649px',
  right: '-2.649px',
  bottom: '-2.649px',
  bg:
    colorScheme === 'light'
      ? 'light.interactive.tertiary-border'
      : 'interactive.tertiary-border',
  clipPath: POLYGON,
  zIndex: 0,
  pointerEvents: 'none',
})

export const containerStyles = (
  colorScheme: ColorScheme,
  maxW?: string,
  size: 'default' | 'compact' = 'default'
): SystemStyleObject => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  bg: colorScheme === 'light' ? 'var(--bg-app, #F0F1F5)' : 'bg.card-elevated',
  borderRadius: '0',
  h: size === 'compact' ? 'auto' : '774px',
  maxH: '90vh',
  w: '90vw',
  maxW: maxW ?? (size === 'compact' ? '404.15px' : '1143.5px'),
  overflow: 'hidden',
})

export const headerStyles: SystemStyleObject = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  px: '24px',
  pt: '32px',
  pb: '0',
  flexShrink: 0,
}

export const titleStyles = (
  colorScheme: ColorScheme,
  size: 'default' | 'compact' = 'default'
): SystemStyleObject => ({
  fontFamily:
    size === 'compact'
      ? "'Soehne Kraftig', sans-serif"
      : "'Soehne Breit', sans-serif",
  fontSize: size === 'compact' ? '24px' : '32px',
  fontWeight: '500',
  lineHeight: size === 'compact' ? '32px' : '40px',
  letterSpacing: size === 'compact' ? '-0.12px' : '-0.32px',
  color: tokens[colorScheme].textPrimary,
  pr: '40px',
})

// Scrollbar thumb per scheme — the UA default scrollbar ignores the
// colorScheme entirely, so scrollable bodies style their own.
const SCROLLBAR_THUMB: Record<ColorScheme, string> = {
  dark: tokenVar('colors.border.strong'),
  light: tokenVar('colors.light.border.strong'),
}

export const bodyStyles = (
  size: 'default' | 'compact' = 'default',
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  flex: 1,
  overflowY: size === 'compact' ? 'hidden' : 'auto',
  px: '24px',
  pt: '36px',
  pb: '24px',
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
  // Standard properties (Firefox, Chrome 121+); WebKit pseudos as fallback.
  scrollbarWidth: 'thin',
  scrollbarColor: `${SCROLLBAR_THUMB[colorScheme]} transparent`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: SCROLLBAR_THUMB[colorScheme],
    borderRadius: '4px',
  },
})

export const headerContentStyles: SystemStyleObject = {
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
}

export const noteStyles: SystemStyleObject = {
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
}

export const footerStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
  px: '24px',
  pb: '24px',
  pt: '30px',
  flexShrink: 0,
  width: '100%',
}

export const catEarStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  w: '14px',
  h: '14px',
  border: 'none',
  cursor: 'pointer',
  background: 'transparent',
  color: colorScheme === 'light' ? '#FFFFFF' : tokens[colorScheme].textPrimary,
  _hover: {
    opacity: 0.7,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: tokens[colorScheme].focusRing,
    outlineOffset: '2px',
  },
})
